/**
 * Tab strip with count badges (NIT-001).
 *
 *   Event Log    — total entries in the ring buffer
 *   Offline      — queued sessions (status === 'queued')
 *   Charge Point — count of connectors with an activeTransactionId
 *
 * Badges hide when the count is 0 (handled by CountBadge).
 */
import { STATION_TABS, type StationTab, useUIStore } from '@/state/useUIStore';
import { useEventLogStore } from '@/state/useEventLogStore';
import { useOfflineQueueStore } from '@/state/useOfflineQueueStore';
import { isV2x } from '@/lib/ocppProfile';
import { useStationStore } from '@/state/useStationStore';
import { useDisplayMessageStore } from '@/state/useDisplayMessageStore';
import { CountBadge } from './CountBadge';
import { OfflineSessionStatus } from '@/state/types';

const LABELS: Record<StationTab, string> = {
  chargepoint: 'Charge Point',
  profiles: 'Charging Profiles',
  offline: 'Offline Sessions',
  replay: 'Replay',
  eventLog: 'Event Log',
  display: 'Display Messages',
  history: 'History',
  snapshots: 'Snapshots',
  settings: 'Settings',
};

export function StationTabs(): JSX.Element {
  const active = useUIStore((s) => s.activeTab);
  const setActive = useUIStore((s) => s.setActiveTab);
  const ocppVersion = useStationStore((s) => s.ocppVersion);
  const eventCount = useEventLogStore((s) => s.entries.length);
  const offlineCount = useOfflineQueueStore(
    (s) => s.entries.filter((e) => e.status === OfflineSessionStatus.Queued).length,
  );
  const activeTxCount = useStationStore(
    (s) => Array.from(s.connectors.values()).filter((c) => c.activeTransactionId !== null).length,
  );
  const displayCount = useDisplayMessageStore((s) => s.messages.length);
  // Display Messages is an OCPP 2.x feature — hide the tab on v1.6.
  const tabs = STATION_TABS.filter((t) => t !== 'display' || isV2x(ocppVersion));

  return (
    <div
      role="tablist"
      aria-label="Station sections"
      className="flex flex-nowrap gap-1 overflow-x-auto border-b border-slate-700 bg-slate-900/40 px-4"
      data-testid="station-tabs"
    >
      {tabs.map((tab) => {
        const isActive = active === tab;
        return (
          <button
            key={tab}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-controls={`tab-panel-${tab}`}
            data-testid={`tab-${tab}`}
            onClick={() => setActive(tab)}
            className={
              isActive
                ? 'border-b-2 border-primary-base px-3 py-2 text-sm font-medium text-slate-100'
                : 'border-b-2 border-transparent px-3 py-2 text-sm text-slate-400 hover:text-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-400'
            }
          >
            {LABELS[tab]}
            {tab === 'eventLog' ? (
              <CountBadge count={eventCount} tone="slate" testId="tab-eventLog" />
            ) : null}
            {tab === 'offline' ? (
              <CountBadge count={offlineCount} tone="amber" testId="tab-offline" />
            ) : null}
            {tab === 'chargepoint' ? (
              <CountBadge count={activeTxCount} tone="emerald" testId="tab-chargepoint" />
            ) : null}
            {tab === 'display' ? (
              <CountBadge count={displayCount} tone="slate" testId="tab-display" />
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
